import { DEFAULT_LOCALE, type AppLocale } from "@/lib/i18n/constants";
import { isContentStaticField, isContentTextField, isRecord } from "./schema";
import type { ContentEntry, ContentTextField, FieldProvenance } from "./types";

export type ContentValidationIssue = {
  readonly code:
    | "raw-string"
    | "missing-status"
    | "missing-required"
    | "english-fallback"
    | "human-overwrite"
    | "language-mismatch";
  readonly path: string;
  readonly message: string;
};

export class ContentValidationError extends Error {
  readonly issues: readonly ContentValidationIssue[];

  constructor(message: string, issues: readonly ContentValidationIssue[]) {
    super(message);
    this.name = "ContentValidationError";
    this.issues = issues;
  }
}

export function validateContentEntry(entry: ContentEntry): ContentValidationIssue[] {
  const issues: ContentValidationIssue[] = [];

  walkContentValue(entry.base, "base", issues);

  for (const [locale, override] of Object.entries(entry.localeOverrides ?? {})) {
    walkContentValue(override, `localeOverrides.${locale}`, issues);
  }

  for (const [persona, override] of Object.entries(entry.personaOverrides ?? {})) {
    walkContentValue(override, `personaOverrides.${persona}`, issues);
  }

  for (const [locale, personaOverrides] of Object.entries(entry.localePersonaOverrides ?? {})) {
    for (const [persona, override] of Object.entries(personaOverrides ?? {})) {
      walkContentValue(override, `localePersonaOverrides.${locale}.${persona}`, issues);
    }
  }

  for (const [surface, override] of Object.entries(entry.surfaceOverrides ?? {})) {
    walkContentValue(override, `surfaceOverrides.${surface}`, issues);
  }

  for (const [device, override] of Object.entries(entry.deviceOverrides ?? {})) {
    walkContentValue(override, `deviceOverrides.${device}`, issues);
  }

  return issues;
}

export function assertValidContentEntry(entry: ContentEntry): void {
  const issues = validateContentEntry(entry);

  if (issues.length > 0) {
    throw new ContentValidationError(`Content entry ${entry.key} has invalid field markers.`, issues);
  }
}

export function assertCanRunTranslationAutomation(entries: readonly ContentEntry[]): void {
  const issues = entries.flatMap(validateContentEntry);

  if (issues.length > 0) {
    throw new ContentValidationError("Translation automation cannot run until every text field has status markers.", issues);
  }
}

export function assertMachineTranslationWriteAllowed(existing: ContentTextField, path = "field"): void {
  if (existing._status === "human") {
    throw new ContentValidationError("Machine translation cannot overwrite human-reviewed content.", [
      {
        code: "human-overwrite",
        path,
        message: `Human-reviewed content at ${path} must be changed by a human or explicitly downgraded first.`,
      },
    ]);
  }
}

export function validateRequiredResolvedContent({
  entry,
  content,
  provenance,
  locale,
  indexable,
  allowPreviewFallback,
}: {
  entry: ContentEntry;
  content: unknown;
  provenance: Record<string, FieldProvenance>;
  locale: AppLocale;
  indexable: boolean;
  allowPreviewFallback: boolean;
}): ContentValidationIssue[] {
  const issues: ContentValidationIssue[] = [];

  for (const field of entry.requiredFields) {
    const value = getPath(content, field.path);

    if (isMissingResolvedValue(value)) {
      issues.push({
        code: "missing-required",
        path: field.path,
        message: `Required ${field.kind} content is missing for ${entry.key}.`,
      });
      continue;
    }

    const requiredProvenance = collectProvenanceForPath(field.path, value, provenance);
    const shouldBlockEnglishFallback = indexable && locale !== DEFAULT_LOCALE && !allowPreviewFallback;

    for (const item of requiredProvenance) {
      if (item.status === "missing") {
        issues.push({
          code: "missing-required",
          path: item.path,
          message: `Required ${field.kind} content at ${item.path} is marked missing for ${entry.key}.`,
        });
      }

      const isTranslatable = item.status !== undefined || item.lang !== undefined;

      if (shouldBlockEnglishFallback && isTranslatable && (item.usedFallback || item.lang !== locale)) {
        issues.push({
          code: item.lang === locale ? "english-fallback" : "language-mismatch",
          path: item.path,
          message: `Indexable ${locale} content cannot use ${item.lang ?? "unknown"} fallback at ${item.path}.`,
        });
      }
    }
  }

  return issues;
}

function walkContentValue(value: unknown, path: string, issues: ContentValidationIssue[]): void {
  if (typeof value === "string") {
    issues.push({
      code: "raw-string",
      path,
      message: `Raw string at ${path} must be wrapped with text() or staticText().`,
    });
    return;
  }

  if (isContentTextField(value)) {
    if (!value._status || !value._lang) {
      issues.push({
        code: "missing-status",
        path,
        message: `Translatable field at ${path} must include _status and _lang markers.`,
      });
    }
    return;
  }

  if (isContentStaticField(value) || value === null || typeof value === "number" || typeof value === "boolean") {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item, index) => walkContentValue(item, `${path}.${index}`, issues));
    return;
  }

  if (isRecord(value)) {
    for (const [key, item] of Object.entries(value)) {
      walkContentValue(item, `${path}.${key}`, issues);
    }
  }
}

function getPath(value: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, segment) => {
    if (current === undefined || current === null) return undefined;
    if (Array.isArray(current)) return current[Number(segment)];
    if (isRecord(current)) return current[segment];
    return undefined;
  }, value);
}

function isMissingResolvedValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0 || value.every(isMissingResolvedValue);
  if (isRecord(value)) return Object.keys(value).length === 0;

  return false;
}

function collectProvenanceForPath(path: string, value: unknown, provenance: Record<string, FieldProvenance>): FieldProvenance[] {
  if (provenance[path]) {
    return [provenance[path]];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item, index) => collectProvenanceForPath(`${path}.${index}`, item, provenance));
  }

  if (isRecord(value)) {
    return Object.entries(value).flatMap(([key, item]) => collectProvenanceForPath(`${path}.${key}`, item, provenance));
  }

  return [];
}
